/**
 * Turn characteristics-estimation `raw_input` into markdown for `react-markdown`.
 *
 * The current backend emits markdown directly (see `generate_address_report` in
 * `BuildingDataHelpers.py`). Older estimations were stored in the previous
 * "Main Address Searched For: ... Property Information: { ... }" plain-text
 * layout, so this module also provides a converter that parses the legacy
 * blocks back into structured data and reserializes them as markdown that
 * mirrors the new backend output.
 */

const MARKDOWN_MARKERS = ['# Adresrapport', '# Address report'];

function isValueExplanation(
    v: unknown,
): v is { value: unknown; explanation: string } {
    return (
        v !== null &&
        typeof v === 'object' &&
        !Array.isArray(v) &&
        Object.keys(v as object).length === 2 &&
        'value' in (v as object) &&
        'explanation' in (v as object)
    );
}

function formatScalar(v: unknown): string {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'boolean') return v ? 'ja' : 'nee';
    return String(v);
}

function indentString(depth: number): string {
    return '  '.repeat(depth);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function dataToMarkdown(data: unknown, depth = 0): string {
    if (isPlainObject(data)) return dictToBullets(data, depth);
    if (Array.isArray(data)) return listToBullets(data, depth);
    return `${indentString(depth)}${formatScalar(data)}`;
}

function dictToBullets(data: Record<string, unknown>, depth = 0): string {
    const keys = Object.keys(data);
    if (keys.length === 0) return `${indentString(depth)}_(leeg)_`;
    const indent = indentString(depth);
    const parts: string[] = [];
    for (const key of keys) {
        const value = data[key];
        if (isPlainObject(value) && Object.keys(value).length > 0) {
            parts.push(`${indent}- **${key}:**`);
            parts.push(dictToBullets(value, depth + 1));
        } else if (Array.isArray(value) && value.length > 0) {
            if (value.every((x) => !isPlainObject(x) && !Array.isArray(x))) {
                const inline = value.map((item) => formatScalar(item)).join(', ');
                parts.push(`${indent}- **${key}:** ${inline}`);
            } else {
                parts.push(`${indent}- **${key}:**`);
                parts.push(listToBullets(value, depth + 1));
            }
        } else {
            parts.push(`${indent}- **${key}:** ${formatScalar(value)}`);
        }
    }
    return parts.join('\n');
}

function listToBullets(items: unknown[], depth = 0): string {
    if (items.length === 0) return `${indentString(depth)}_(leeg)_`;
    const indent = indentString(depth);
    if (items.every((x) => !isPlainObject(x) && !Array.isArray(x))) {
        return items.map((item) => `${indent}- ${formatScalar(item)}`).join('\n');
    }
    const parts: string[] = [];
    items.forEach((item, idx) => {
        if (isPlainObject(item)) {
            parts.push(`${indent}- **#${idx + 1}**`);
            parts.push(dictToBullets(item, depth + 1));
        } else if (Array.isArray(item)) {
            parts.push(`${indent}- **#${idx + 1}**`);
            parts.push(listToBullets(item, depth + 1));
        } else {
            parts.push(`${indent}- ${formatScalar(item)}`);
        }
    });
    return parts.join('\n');
}

function renderFieldBullet(
    name: string,
    payload: { value: unknown; explanation: string },
): string {
    const { value, explanation } = payload;
    if (isPlainObject(value) || Array.isArray(value)) {
        const lines = [`- **${name}:**`, dataToMarkdown(value, 1)];
        if (explanation) lines.push(`  _${explanation}_`);
        return lines.join('\n');
    }
    const line = `- **${name}:** ${formatScalar(value)}`;
    if (explanation) return `${line}  \n  _${explanation}_`;
    return line;
}

function renderAdresinfoItems(items: unknown[]): string {
    if (items.length === 0) return '_Geen adresgegevens._';
    const parts: string[] = [];
    items.forEach((item, idx) => {
        parts.push(`#### Adres ${idx + 1}`);
        if (!isPlainObject(item)) {
            parts.push(formatScalar(item));
            return;
        }
        const rows: string[] = [];
        for (const [key, value] of Object.entries(item)) {
            if (isValueExplanation(value)) {
                rows.push(renderFieldBullet(key, value));
            } else if (isPlainObject(value) || Array.isArray(value)) {
                rows.push(`- **${key}:**`);
                rows.push(dataToMarkdown(value, 1));
            } else {
                rows.push(`- **${key}:** ${formatScalar(value)}`);
            }
        }
        parts.push(rows.join('\n'));
    });
    return parts.join('\n\n');
}

function renderPropertySection(rendered: Record<string, unknown>): string {
    const keys = Object.keys(rendered);
    if (keys.length === 0) return '_Geen pandinformatie beschikbaar._';
    const simpleLines: string[] = [];
    const subSections: string[] = [];
    for (const key of keys) {
        const value = rendered[key];
        if (isValueExplanation(value)) {
            simpleLines.push(renderFieldBullet(key, value));
        } else if (key === 'adresinfo' && Array.isArray(value)) {
            subSections.push(`### ${key}\n\n${renderAdresinfoItems(value)}`);
        } else if (isPlainObject(value) || Array.isArray(value)) {
            simpleLines.push(`- **${key}:**`);
            simpleLines.push(dataToMarkdown(value, 1));
        } else {
            simpleLines.push(`- **${key}:** ${formatScalar(value)}`);
        }
    }
    const parts: string[] = [];
    if (simpleLines.length > 0) parts.push(simpleLines.join('\n'));
    parts.push(...subSections);
    return parts.join('\n\n');
}

function renderDictOrEmpty(value: unknown, emptyText: string): string {
    if (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0) ||
        (isPlainObject(value) && Object.keys(value).length === 0)
    ) {
        return emptyText;
    }
    if (isPlainObject(value) || Array.isArray(value)) {
        return dataToMarkdown(value);
    }
    return formatScalar(value);
}

/** Brace/bracket-aware extractor that scans for the first JSON value after `header`. */
function extractJsonBlock(raw: string, header: string): unknown {
    const idx = raw.indexOf(header);
    if (idx === -1) return null;
    const after = idx + header.length;
    const brace = raw.indexOf('{', after);
    const bracket = raw.indexOf('[', after);
    let start = -1;
    let opener = '';
    if (brace !== -1 && (bracket === -1 || brace < bracket)) {
        start = brace;
        opener = '{';
    } else if (bracket !== -1) {
        start = bracket;
        opener = '[';
    }
    if (start === -1) return null;
    const closer = opener === '{' ? '}' : ']';
    let depth = 0;
    let inString = false;
    let escape = false;
    for (let i = start; i < raw.length; i++) {
        const c = raw[i];
        if (escape) {
            escape = false;
            continue;
        }
        if (c === '\\') {
            escape = true;
            continue;
        }
        if (c === '"') {
            inString = !inString;
            continue;
        }
        if (inString) continue;
        if (c === opener) depth++;
        else if (c === closer) {
            depth--;
            if (depth === 0) {
                const block = raw.slice(start, i + 1);
                try {
                    return JSON.parse(block);
                } catch {
                    return null;
                }
            }
        }
    }
    return null;
}

function extractMeldings(raw: string): string[] {
    const header = 'Meldings Between the Operator and Firefighters:';
    const idx = raw.indexOf(header);
    if (idx === -1) return [];
    return raw
        .slice(idx + header.length)
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
}

export function isLegacyAddressReport(raw: string): boolean {
    return !MARKDOWN_MARKERS.some((marker) => raw.includes(marker)) && raw.includes('Property Information:');
}

export function legacyAddressReportToMarkdown(raw: string): string {
    const searched = raw.match(/Main Address Searched For:\s*(.+)/);
    const searchedText = searched ? searched[1].trim() : '';
    const [zipcode = '', houseNumber = ''] = searchedText.split(/\s+/);

    const mainAddress = extractJsonBlock(raw, 'Main Address Information:');
    const propertyInfo = extractJsonBlock(raw, 'Property Information:');
    const adjacentAddresses = extractJsonBlock(
        raw,
        'The following addresses are also part of the same property:',
    );
    const openSpace = extractJsonBlock(raw, 'Open Space Information:');
    const meldings = extractMeldings(raw);

    const sections: string[] = [
        '# Adresrapport',
        '## Gezocht hoofdadres',
        `- **Postcode:** ${zipcode || '—'}\n- **Huisnummer:** ${houseNumber || '—'}`,
        '## Hoofdadresgegevens',
        renderDictOrEmpty(mainAddress, '_Geen hoofdadresgegevens beschikbaar._'),
        '## Pandinformatie',
        renderPropertySection((propertyInfo as Record<string, unknown>) ?? {}),
        '## Overige objecten op dit perceel',
        renderDictOrEmpty(
            adjacentAddresses,
            '_Geen overige objecten op dit perceel._',
        ),
        '## Openbare ruimte',
        renderDictOrEmpty(openSpace, '_Geen openbare-ruimtegegevens beschikbaar._'),
        '## GMS informatie van de meldkamer',
        meldings.length > 0
            ? meldings.map((line) => `- ${line}`).join('\n')
            : '_Geen GMS-informatie beschikbaar._',
    ];

    return sections.join('\n\n');
}

export function formatAddressReportForDisplay(
    raw: string | null | undefined,
): string {
    if (!raw) return '';
    if (MARKDOWN_MARKERS.some((marker) => raw.includes(marker))) return raw;
    if (isLegacyAddressReport(raw)) return legacyAddressReportToMarkdown(raw);
    return raw;
}
